import { getGroupsIn } from '@db/Groups/Get';
import {
  addComment,
  addPlan,
  deletePlan,
  getPlan,
  getPlanComments,
  getPlanContactInviteIds,
  getPlanLikes,
  getPlanUserInviteIds,
  likePlan,
  unlikePlan,
  updatePlan,
  userLikesPlan,
} from '@db/Plans';
import { getUsersIn } from '@db/Users/Get';
import { getErrorProps } from '@helpers/Errors';
import { sortBy, sortByDisplayName } from '@helpers/Formatters';
import {
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryIdListKeyPart,
  createQueryKey,
  nullableQueryKeyPart,
  useAppMutation,
  useAppQuery,
  useAppQueryCache,
} from '@helpers/query';
import type { AppQueryCache, AppQueryKey, AppQueryKeyValue } from '@helpers/query';
import type { Comment, Group, Person, Plan, PlanFormData, User } from '@types';

import type { UpdatePlanData } from './Update';

const EMPTY_COMMENTS: Comment[] = [];
const EMPTY_GROUPS: Group[] = [];
const EMPTY_INVITE_IDS: string[] = [];
const EMPTY_USERS: User[] = [];

export type UsePlanQueryOptions = {
  /** Set false while route params are not ready. */
  enabled?: boolean;
  planId?: string;
  /** Value that forces a fresh plan query for the same plan id. */
  refreshKey?: AppQueryKeyValue;
};

export type UsePlanRelatedQueryOptions = {
  /** Plan whose related documents should be loaded. */
  plan?: Plan | null;
};

export type UsePlanIdQueryOptions = {
  /** Set false while route params are not ready. */
  enabled?: boolean;
  planId?: string;
};

export type CreatePlanMutationVariables = {
  /** Group ids that should receive the newly created plan. */
  groupIds: string[];
  /** People to invite to the newly created plan. */
  people: Person[];
  /** Plan fields submitted by the create plan form. */
  plan: PlanFormData;
};

export type UpdatePlanMutationVariables = {
  /** Group ids that should remain associated with the plan. */
  groupIds: string[];
  /** People to invite to the updated plan. */
  people: Person[];
  /** Complete editable plan fields submitted by the update plan form. */
  plan: UpdatePlanData;
  /** Plan being updated. */
  planId: string;
};

export type DeletePlanMutationVariables = {
  /** Plan being deleted. */
  planId: string;
};

export type AddPlanCommentMutationVariables = {
  /** Comment text to post. */
  comment: string;
  /** Plan receiving the comment. */
  planId: string;
};

export function usePlanQuery({ enabled = true, planId, refreshKey }: UsePlanQueryOptions) {
  const isEnabled = enabled && Boolean(planId);
  const query = useAppQuery<Plan>({
    queryKey: createQueryKey(
      AppQueryDomain.PLANS,
      AppQueryKeySegment.DETAIL,
      planId ?? null,
      nullableQueryKeyPart(refreshKey)
    ),
    queryFn: () => getPlan(planId ?? ''),
    enabled: isEnabled,
    retry: false,
  });

  return {
    error: query.isLoadingError,
    errorProps:
      query.error && query.isLoadingError
        ? getErrorProps(query.error, { onRetry: query.refetch })
        : undefined,
    loading: isEnabled && query.isLoading,
    plan: isEnabled ? (query.data ?? null) : null,
  };
}

export function usePlanMembersQuery({ plan }: UsePlanRelatedQueryOptions) {
  const userIds = getPlanMemberUserIds(plan);
  const isEnabled = Boolean(plan?.planId) && userIds.length > 0;
  const query = useAppQuery<User[]>({
    queryKey: createQueryKey(
      AppQueryDomain.PLANS,
      AppQueryKeySegment.MEMBERS,
      plan?.planId ?? null,
      createQueryIdListKeyPart(userIds)
    ),
    queryFn: async () => sortByDisplayName(await getUsersIn(userIds)),
    enabled: isEnabled,
    retry: false,
  });

  return {
    loading: isEnabled && query.isLoading,
    users: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
  };
}

export function usePlanGroupsQuery({ plan }: UsePlanRelatedQueryOptions) {
  const groupIds = createQueryIdListKeyPart(plan?.groupIds);
  const isEnabled = Boolean(plan?.planId) && groupIds.length > 0;
  const query = useAppQuery<Group[]>({
    queryKey: createQueryKey(
      AppQueryDomain.PLANS,
      AppQueryKeySegment.GROUPS,
      plan?.planId ?? null,
      groupIds
    ),
    queryFn: async () => sortByDisplayName(await getGroupsIn([...groupIds])),
    enabled: isEnabled,
    retry: false,
  });

  return {
    groups: isEnabled ? (query.data ?? EMPTY_GROUPS) : EMPTY_GROUPS,
    loading: isEnabled && query.isLoading,
  };
}

export function usePlanCommentsQuery({ enabled = true, planId }: UsePlanIdQueryOptions) {
  const isEnabled = enabled && Boolean(planId);
  const query = useAppQuery<Comment[]>({
    queryKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.COMMENTS, planId ?? null),
    queryFn: async () => sortBy(await getPlanComments(planId ?? ''), 'createdAt', 'desc'),
    enabled: isEnabled,
    retry: false,
  });

  return {
    comments: isEnabled ? (query.data ?? EMPTY_COMMENTS) : EMPTY_COMMENTS,
    loading: isEnabled && query.isLoading,
    reload: async () => {
      await query.refetch();
    },
  };
}

export function usePlanLikesQuery({ enabled = true, planId }: UsePlanIdQueryOptions) {
  const isEnabled = enabled && Boolean(planId);
  const query = useAppQuery<User[]>({
    queryKey: createPlanLikesQueryKey(planId),
    queryFn: async () => {
      const likes = await getPlanLikes(planId ?? '');
      const likeUserIds = likes.map((like) => like.userId);
      if (likeUserIds.length === 0) return [];

      return sortByDisplayName(await getUsersIn(likeUserIds));
    },
    enabled: isEnabled,
    retry: false,
  });

  return {
    likeUsers: isEnabled ? (query.data ?? EMPTY_USERS) : EMPTY_USERS,
    loading: isEnabled && query.isLoading,
  };
}

export function usePlanInviteIdsQuery({ enabled = true, planId }: UsePlanIdQueryOptions) {
  const isEnabled = enabled && Boolean(planId);
  const query = useAppQuery<string[]>({
    queryKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.INVITES, planId ?? null),
    queryFn: async () => {
      const [userInviteIds, contactInviteIds] = await Promise.all([
        getPlanUserInviteIds(planId ?? ''),
        getPlanContactInviteIds(planId ?? ''),
      ]);

      return [...userInviteIds, ...contactInviteIds];
    },
    enabled: isEnabled,
    retry: false,
  });

  return {
    inviteIds: isEnabled ? (query.data ?? EMPTY_INVITE_IDS) : EMPTY_INVITE_IDS,
    loading: isEnabled && query.isLoading,
  };
}

export function useCreatePlanMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<string, Error, CreatePlanMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.CREATE_MUTATION),
    mutationFn: ({ groupIds, people, plan }) => addPlan(plan, groupIds, people),
    onSuccess: async () => {
      await invalidatePlanMutationQueries(queryCache);
    },
  });
}

export function useUpdatePlanMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<string, Error, UpdatePlanMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.UPDATE_MUTATION),
    mutationFn: ({ groupIds, people, plan, planId }) => updatePlan(planId, plan, groupIds, people),
    onSuccess: async () => {
      await invalidatePlanMutationQueries(queryCache);
    },
  });
}

export function useDeletePlanMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, DeletePlanMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.DELETE_MUTATION),
    mutationFn: ({ planId }) => deletePlan(planId),
    onSuccess: async () => {
      await invalidatePlanMutationQueries(queryCache);
    },
  });
}

export function useAddPlanCommentMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, AddPlanCommentMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.ADD_COMMENT_MUTATION),
    mutationFn: ({ comment, planId }) => addComment(comment, planId),
    onSuccess: async (_data, { planId }) => {
      await queryCache.invalidateQueries(createPlanCommentsQueryKey(planId));
    },
  });
}

export type UsePlanLikeStatusQueryOptions = {
  /** Set false while auth or route params are not ready. */
  enabled?: boolean;
  planId?: string;
  userId?: string;
};

export function usePlanLikeStatusQuery({
  enabled = true,
  planId,
  userId,
}: UsePlanLikeStatusQueryOptions) {
  const isEnabled = enabled && Boolean(planId) && Boolean(userId);
  const query = useAppQuery<boolean>({
    queryKey: createPlanLikeStatusQueryKey(planId, userId),
    queryFn: async () => Boolean(await userLikesPlan(userId ?? '', planId ?? '')),
    enabled: isEnabled,
    retry: false,
  });

  return {
    loading: isEnabled && query.isLoading,
    userLikes: isEnabled ? Boolean(query.data) : false,
  };
}

export type UsePlanLikeMutationOptions = {
  /** Current user, used to optimistically update the visible likes list when it is cached. */
  currentUser?: User | null;
  /** Plan whose like document should be mutated. */
  planId: string;
  /** User whose like status cache should be updated. */
  userId: string;
};

export type PlanLikeMutationVariables = {
  /** The desired final like state for the current user. */
  userLikes: boolean;
};

type PlanLikeMutationContext = {
  previousLikeUsers?: User[];
  previousUserLikes?: boolean;
};

export function usePlanLikeMutation({ currentUser, planId, userId }: UsePlanLikeMutationOptions) {
  const queryCache = useAppQueryCache();
  const likeStatusQueryKey = createPlanLikeStatusQueryKey(planId, userId);
  const likesQueryKey = createPlanLikesQueryKey(planId);

  return useAppMutation<void, Error, PlanLikeMutationVariables, PlanLikeMutationContext>({
    mutationKey: createQueryKey(
      AppQueryDomain.PLANS,
      AppQueryKeySegment.LIKE_MUTATION,
      planId,
      userId
    ),
    mutationFn: ({ userLikes }) => (userLikes ? likePlan(planId) : unlikePlan(planId)),
    onMutate: async ({ userLikes }) => {
      await Promise.all([
        queryCache.cancelQueries(likeStatusQueryKey),
        queryCache.cancelQueries(likesQueryKey),
      ]);

      const previousUserLikes = queryCache.getQueryData<boolean>(likeStatusQueryKey);
      const previousLikeUsers = queryCache.getQueryData<User[]>(likesQueryKey);

      queryCache.setQueryData<boolean>(likeStatusQueryKey, userLikes);
      if (currentUser) {
        queryCache.setQueryData<User[]>(likesQueryKey, (users) =>
          updateCachedPlanLikeUsers(users, currentUser, userLikes)
        );
      }

      return { previousLikeUsers, previousUserLikes };
    },
    onError: (_error, _variables, context) => {
      restoreCachedQueryData(queryCache, likeStatusQueryKey, context?.previousUserLikes);
      restoreCachedQueryData(queryCache, likesQueryKey, context?.previousLikeUsers);
    },
    onSettled: async () => {
      await queryCache.invalidateQueries(createQueryKey(AppQueryDomain.PLANS));
    },
  });
}

function getPlanMemberUserIds(plan?: Plan | null) {
  if (!plan) return [];

  return [...new Set([plan.userId, ...(plan.userIds ?? [])].filter((userId) => userId.length > 0))];
}

function createPlanLikesQueryKey(planId?: string | null) {
  return createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.LIKES, planId ?? null);
}

function createPlanCommentsQueryKey(planId?: string | null) {
  return createQueryKey(AppQueryDomain.PLANS, AppQueryKeySegment.COMMENTS, planId ?? null);
}

function createPlanLikeStatusQueryKey(planId?: string | null, userId?: string | null) {
  return createQueryKey(
    AppQueryDomain.PLANS,
    AppQueryKeySegment.LIKE_STATUS,
    planId ?? null,
    userId ?? null
  );
}

async function invalidatePlanMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.PLANS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.GROUPS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}

function restoreCachedQueryData<TData>(
  queryCache: AppQueryCache,
  queryKey: AppQueryKey,
  previousData: TData | undefined
) {
  if (previousData === undefined) {
    queryCache.removeQueries(queryKey);
    return;
  }

  queryCache.setQueryData<TData>(queryKey, previousData);
}

function updateCachedPlanLikeUsers(
  users: User[] | undefined,
  currentUser: User,
  userLikes: boolean
) {
  const currentUsers = users ?? EMPTY_USERS;
  if (!userLikes) {
    return currentUsers.filter((user) => user.userId !== currentUser.userId);
  }

  if (currentUsers.some((user) => user.userId === currentUser.userId)) {
    return currentUsers;
  }

  return sortByDisplayName([...currentUsers, currentUser]);
}
